/**
 * Clinic locator: ZIP search + sort by distance (Contentful location field).
 */
(function () {
	const RESULTS_LIMIT = 8;

	const locator = document.getElementById('clinic-locator');
	if (!locator) {
		return;
	}

	const clinics = parseClinics(locator.getAttribute('data-clinics'));
	const form = locator.querySelector('#clinic-locator-search');
	const zipInput = locator.querySelector('#clinic-locator-zip');
	const messageEl = locator.querySelector('#clinic-locator-message');
	const resultsEl = locator.querySelector('#clinic-list-results');
	const headingEl = locator.querySelector('#clinic-results-heading');
	const viewAllBtn = locator.querySelector('#clinic-view-all');
	const disclaimerAccordion = locator.querySelector('#clinic-disclaimer-accordion');
	const disclaimerTrigger = locator.querySelector('#clinic-disclaimer-accordion-trigger');
	const resultsContainer = locator.querySelector('.clinics-results-container');

	const icons = {
		location: locator.getAttribute('data-icon-location') || '',
		phone: locator.getAttribute('data-icon-phone') || '',
		website: locator.getAttribute('data-icon-website') || '',
	};

	if (!form || !zipInput || !resultsEl || !clinics.length) {
		return;
	}

	let lastSortedClinics = null;
	let isExpanded = false;

	clearResults();

	form.addEventListener('submit', function (event) {
		event.preventDefault();
		searchByZip();
	});

	if (zipInput) {
		zipInput.addEventListener('input', function () {
			hideMessage();
		});
	}

	if (viewAllBtn) {
		viewAllBtn.addEventListener('click', function () {
			const list = lastSortedClinics || clinics;
			const showDistance = Boolean(lastSortedClinics);

			renderClinics(list, { showDistance: showDistance, limit: null });
			isExpanded = true;
			setViewAllVisible(false);
		});
	}

	if (disclaimerTrigger) {
		disclaimerTrigger.addEventListener('click', function () {
			const isOpen = disclaimerTrigger.getAttribute('aria-expanded') === 'true';
			disclaimerTrigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
		});
	}

	function parseClinics(raw) {
		if (!raw) {
			return [];
		}
		try {
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : [];
		} catch (e) {
			return [];
		}
	}

	function searchByZip() {
		const zip = String(zipInput.value || '').trim();
		hideMessage();

		if (!/^\d{5}$/.test(zip)) {
			showMessage('Please enter a valid 5-digit ZIP code.', true);
			setResultsContainerActive(true);
			return;
		}

		setResultsContainerActive(true);
		setLoading(true);

		fetch('https://api.zippopotam.us/us/' + encodeURIComponent(zip))
			.then(function (response) {
				if (!response.ok) {
					throw new Error('zip_not_found');
				}
				return response.json();
			})
			.then(function (data) {
				const place = data && data.places && data.places[0];
				if (!place || place.latitude === undefined || place.longitude === undefined) {
					throw new Error('zip_not_found');
				}
				const userLat = parseFloat(place.latitude);
				const userLon = parseFloat(place.longitude);
				const sorted = sortClinicsByDistance(clinics, userLat, userLon);
				lastSortedClinics = sorted;
				isExpanded = false;
				setResultsHeadingVisible(true);
				setDisclaimerAccordionVisible(true);
				renderClinics(sorted, { showDistance: true, limit: RESULTS_LIMIT });
				setViewAllVisible(sorted.length > RESULTS_LIMIT);
			})
			.catch(function () {
				showMessage('We could not find that ZIP code. Please try again.', true);
			})
			.finally(function () {
				setLoading(false);
			});
	}

	function sortClinicsByDistance(clinicList, userLat, userLon) {
		const ranked = clinicList.map(function (clinic) {
			const hasCoords =
				typeof clinic.lat === 'number' &&
				typeof clinic.lon === 'number' &&
				!Number.isNaN(clinic.lat) &&
				!Number.isNaN(clinic.lon);
			return {
				clinic: clinic,
				distance: hasCoords ? haversineMiles(userLat, userLon, clinic.lat, clinic.lon) : null,
			};
		});

		ranked.sort(function (a, b) {
			if (a.distance === null && b.distance === null) {
				return a.clinic.name.localeCompare(b.clinic.name);
			}
			if (a.distance === null) {
				return 1;
			}
			if (b.distance === null) {
				return -1;
			}
			if (a.distance !== b.distance) {
				return a.distance - b.distance;
			}
			return a.clinic.name.localeCompare(b.clinic.name);
		});

		return ranked.map(function (item) {
			const next = Object.assign({}, item.clinic);
			if (item.distance !== null) {
				next.distance = Math.round(item.distance * 10) / 10;
			}
			return next;
		});
	}

	function haversineMiles(lat1, lon1, lat2, lon2) {
		const toRad = function (deg) {
			return (deg * Math.PI) / 180;
		};
		const r = 3958.8;
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		return r * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
	}

	function renderClinics(clinicList, options) {
		const showDistance = Boolean(options && options.showDistance);
		const limit = options && Object.prototype.hasOwnProperty.call(options, 'limit') ? options.limit : null;
		const list =
			typeof limit === 'number' && limit > 0 ? clinicList.slice(0, limit) : clinicList;

		resultsEl.innerHTML = list
			.map(function (clinic) {
				return renderClinicCard(clinic, showDistance);
			})
			.join('');
		resultsEl.hidden = list.length === 0;
	}

	function clearResults() {
		resultsEl.innerHTML = '';
		resultsEl.hidden = true;
		setResultsHeadingVisible(false);
		setViewAllVisible(false);
		setDisclaimerAccordionVisible(false);
		setResultsContainerActive(false);
		isExpanded = false;
		lastSortedClinics = null;
	}

	function formatDistance(distance) {
		const label = distance === 1 ? 'mile' : 'miles';
		return String(distance) + ' ' + label;
	}

	function normalizeWebsiteUrl(url) {
		const value = String(url || '').trim();
		if (!value) {
			return '';
		}
		if (!/^https?:\/\//i.test(value)) {
			return 'https://' + value;
		}
		return value;
	}

	function renderClinicCard(clinic, showDistance) {
		let html = '<li class="clinic-card">';
		html += '<div class="clinic-card__header">';
		html += '<h3 class="clinic-card__name">' + escapeHtml(clinic.name) + '</h3>';

		if (showDistance && typeof clinic.distance === 'number') {
			html +=
				'<p class="clinic-card__distance">' +
				escapeHtml(formatDistance(clinic.distance)) +
				'</p>';
		}

		html += '</div>';

		if (clinic.administration_site_only) {
			html +=
				'<p class="clinic-card__badge">' +
				escapeHtml('Administration site only') +
				'</p>';
		}

		html += '<div class="clinic-card__info">';

		if (clinic.address) {
			html += '<div class="clinic-card__row clinic-card__row--address">';
			if (icons.location) {
				html +=
					'<img class="clinic-card__icon" src="' +
					escapeHtml(icons.location) +
					'" alt="" width="12" height="16" />';
			}
			html += '<p class="clinic-card__address">' + escapeHtml(clinic.address) + '</p>';
			html += '</div>';
		}

		if (clinic.phone) {
			const href = String(clinic.phone).replace(/[^0-9+]/g, '');
			html += '<div class="clinic-card__row clinic-card__row--phone">';
			if (icons.phone) {
				html +=
					'<img class="clinic-card__icon" src="' +
					escapeHtml(icons.phone) +
					'" alt="" width="12" height="27" />';
			}
			html +=
				'<p class="clinic-card__phone"><a href="tel:' +
				escapeHtml(href) +
				'">' +
				escapeHtml(clinic.phone) +
				'</a></p>';
			html += '</div>';
		}

		const websiteHref = normalizeWebsiteUrl(clinic.website);
		const websiteLabel = String(clinic.website_label || clinic.website || '').trim();
		if (websiteHref && websiteLabel) {
			html += '<div class="clinic-card__row clinic-card__row--website">';
			if (icons.website) {
				html +=
					'<img class="clinic-card__icon" src="' +
					escapeHtml(icons.website) +
					'" alt="" width="11" height="10" />';
			}
			html +=
				'<p class="clinic-card__website"><a class="exit-modal" href="' +
				escapeHtml(websiteHref) +
				'" target="_blank" rel="noopener noreferrer">' +
				escapeHtml(websiteLabel) +
				'</a></p>';
			html += '</div>';
		}

		html += '</div></li>';
		return html;
	}

	function setResultsHeadingVisible(isVisible) {
		if (headingEl) {
			headingEl.hidden = !isVisible;
		}
	}

	function setViewAllVisible(isVisible) {
		if (viewAllBtn) {
			viewAllBtn.hidden = !isVisible;
		}
	}

	function setDisclaimerAccordionVisible(isVisible) {
		if (!disclaimerAccordion) {
			return;
		}

		disclaimerAccordion.hidden = !isVisible;

		if (isVisible && disclaimerTrigger) {
			disclaimerTrigger.setAttribute('aria-expanded', 'true');
		}
	}

	function setResultsContainerActive(isActive) {
		if (resultsContainer) {
			resultsContainer.classList.toggle('clinics-results-container--active', isActive);
		}
	}

	function escapeHtml(text) {
		return String(text)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
	}

	function showMessage(text, isError) {
		if (!messageEl) {
			return;
		}
		messageEl.textContent = text;
		messageEl.hidden = false;
		messageEl.classList.toggle('clinic-locator__message--error', Boolean(isError));
	}

	function hideMessage() {
		if (!messageEl) {
			return;
		}
		messageEl.hidden = true;
		messageEl.textContent = '';
		messageEl.classList.remove('clinic-locator__message--error');
	}

	function setLoading(isLoading) {
		form.classList.toggle('clinic-locator__search--loading', isLoading);
		const btn = form.querySelector('button[type="submit"]');
		if (btn) {
			btn.disabled = isLoading;
		}
	}
})();
